// UI strings for Sarjan repliikit (routes/Listen.tsx, src/listen/: browse by episode or character,
// a script of lines, the shadow tool of the active line, "Harjoittele näillä") and for the
// rebuilt Ääntäminen (routes/Pronunciation.tsx: the "Lue ääneen" deck and the pronunciation
// path). Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md ("1 repliikki", "2 repliikkiä"; no internal jargon).
import { defineArea } from '../area';

const pad = (n: number) => String(n).padStart(2, '0');

export const showLinesFi = {
  title: 'Sarjan repliikit',
  browse: 'Selaa',
  byEpisode: 'Jakso',
  byCharacter: 'Hahmo',
  lines: (n: number) => (n === 1 ? '1 repliikki' : `${n} repliikkiä`),
  /** "GoT · kausi 3" over the season's episode rows. */
  season: (show: string, s: number) => `${show} · kausi ${s}`,
  /** An episode row and an episode script's title: "3×04", "GoT 3×04". */
  episode: (season: number, episode: number) => `${season}×${pad(episode)}`,
  episodeTitle: (show: string, season: number, episode: number) => `${show} ${season}×${pad(episode)}`,
  targaryens: 'Targaryenit',
  others: 'Muut hahmot',
  week: {
    /** The row on top of the browser. */
    row: 'Tämän viikon repliikit',
    /** A week's script title (?viikko=N). */
    title: (n: number) => `Viikon ${n} repliikit`,
    locked: (n: number) => `Viikko ${n} ei ole vielä auki teille.`,
    none: (n: number) => `Viikossa ${n} ei ole sarjan repliikkejä.`,
    /** The week's Kuuntele step in the lesson (/lessons/<id>/listen). */
    step: (n: number) => `Oppitunti ${n} · Kuuntele`,
    /** On the lesson's Kuuntele step: the same lines here. */
    open: 'Avaa Sarjan repliikeissä',
  },
  notFound: 'Näitä repliikkejä ei löydy.',
  backToList: 'Kaikki repliikit',
  /** What audio a line has, at its top right. */
  source: {
    peterson: 'Peterson',
    actor: 'näyttelijä',
    ours: 'meidän ääntämys',
    none: 'ei äänitettä',
  },
  /** The line's own toggle in the script ("Avaa repliikki: Daenerys"). */
  openLine: (who: string) => `Avaa repliikki: ${who}`,
  /** A script row's head: its place in the list, its ▶ (the model, text still hidden) and the lane's title. */
  row: {
    position: (i: number, n: number) => `${i}/${n}`,
    play: (i: number, n: number) => `Kuuntele repliikki ${i}/${n}`,
    pause: (i: number, n: number) => `Tauko: repliikki ${i}/${n}`,
    /** The clip's length, for screen readers ("kesto 0:04"). */
    length: (len: string) => `kesto ${len}`,
    title: (i: number, n: number, who: string) => `Repliikki ${i}/${n} · ${who}`,
  },
  tool: {
    label: 'Varjosta repliikkiä',
    model: 'Malli',
    modelBy: (who: string) => `Malli · ${who}`,
    actor: 'Näyttelijä',
    /** Under the actor's button: never a model. */
    actorNote: 'näyttelijä · kuuntelua varten',
    record: 'Äänitä',
    stop: 'Lopeta',
    recording: 'Äänitetään…',
    compare: 'Vertaa',
    /** What Vertaa does, as its tooltip / description. */
    compareHint: 'Malli ja sinun äänityksesi peräkkäin',
    noTake: 'Äänitä ensin, niin voit verrata.',
    yours: 'Sinun äänityksesi',
    save: 'Tallenna kumppanin kuultavaksi',
    saved: 'Tallennettu.',
    discard: 'Hylkää',
    noModel: 'Tälle repliikille ei ole vielä äänitettä.',
    /** Keyboard hints in the buttons' tooltips (PC). */
    keyModel: 'Välilyönti',
    keyRecord: 'R',
    keyCompare: 'V',
  },
  practice: {
    h: 'Harjoittele näillä',
    noAudio: 'Näillä repliikeillä ei ole vielä äänitettä, joten kuunteluharjoituksia ei voi tehdä.',
    noOpen: 'Näissä ei vielä ole repliikkejä, joiden sanoista vähintään 80\u00a0% on tullut sinulle vastaan. Valitse kaikki repliikit.',
    noKinds: 'Valitse ainakin yksi tehtävä.',
  },
  /** The partner's dictation, when it stands in the margin. */
  partnerH: 'Kumppanisi äänellä',
  // ---- Ääntäminen ----
  pr: {
    deck: 'Lue ääneen',
    position: (i: number, n: number) => `${i}/${n}`,
    prev: 'Edellinen',
    next: 'Seuraava',
    path: 'Ääntämisen polku',
    mine: 'äänitit',
    partner: (name: string) => `${name} äänitti`,
    notYet: 'ei vielä äänitetty',
    yourTakes: 'Sinun äänityksesi',
    partnerTakes: (name: string) => `${name}: äänitykset`,
    noTakes: 'Ei vielä äänityksiä.',
    sounds: 'Äänteet ja paino: Kielioppi § 1',
    sentencesEmpty: 'Lausepinoon',
    empty: 'Petersonin äänitteitä ladataan…',
  },
};

export const showLinesEn: typeof showLinesFi = {
  title: 'Show lines',
  browse: 'Browse',
  byEpisode: 'Episode',
  byCharacter: 'Character',
  lines: (n) => (n === 1 ? '1 line' : `${n} lines`),
  season: (show, s) => `${show} · season ${s}`,
  episode: (season, episode) => `${season}×${pad(episode)}`,
  episodeTitle: (show, season, episode) => `${show} ${season}×${pad(episode)}`,
  targaryens: 'Targaryens',
  others: 'Other characters',
  week: {
    row: 'This week’s lines',
    title: (n) => `Lines of week ${n}`,
    locked: (n) => `Week ${n} is not open for you yet.`,
    none: (n) => `Week ${n} has no lines from the shows.`,
    step: (n) => `Lesson ${n} · Listen`,
    open: 'Open in Show lines',
  },
  notFound: 'These lines were not found.',
  backToList: 'All lines',
  source: {
    peterson: 'Peterson',
    actor: 'actor',
    ours: 'our pronunciation',
    none: 'no recording',
  },
  openLine: (who) => `Open the line: ${who}`,
  row: {
    position: (i, n) => `${i}/${n}`,
    play: (i, n) => `Listen to line ${i}/${n}`,
    pause: (i, n) => `Pause: line ${i}/${n}`,
    length: (len) => `length ${len}`,
    title: (i, n, who) => `Line ${i}/${n} · ${who}`,
  },
  tool: {
    label: 'Shadow the line',
    model: 'Model',
    modelBy: (who) => `Model · ${who}`,
    actor: 'Actor',
    actorNote: 'actor · for listening',
    record: 'Record',
    stop: 'Stop',
    recording: 'Recording…',
    compare: 'Compare',
    compareHint: 'The model and your take back to back',
    noTake: 'Record first, then you can compare.',
    yours: 'Your take',
    save: 'Save for your partner to hear',
    saved: 'Saved.',
    discard: 'Discard',
    noModel: 'This line has no recording yet.',
    keyModel: 'Space',
    keyRecord: 'R',
    keyCompare: 'V',
  },
  practice: {
    h: 'Practise with these',
    noAudio: 'These lines have no recording yet, so there is nothing to listen to.',
    noOpen: 'None of these lines has at least 80 % of its words introduced to you yet. Choose all lines.',
    noKinds: 'Choose at least one task.',
  },
  partnerH: 'In your partner’s voice',
  pr: {
    deck: 'Read aloud',
    position: (i, n) => `${i}/${n}`,
    prev: 'Previous',
    next: 'Next',
    path: 'Pronunciation path',
    mine: 'you recorded',
    partner: (name) => `${name} recorded`,
    notYet: 'not recorded yet',
    yourTakes: 'Your takes',
    partnerTakes: (name) => `${name}: takes`,
    noTakes: 'No takes yet.',
    sounds: 'Sounds and stress: Grammar § 1',
    sentencesEmpty: 'To the sentence stack',
    empty: 'Loading Peterson’s recordings…',
  },
};

// Messages.showLines (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('showLines', showLinesFi, showLinesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof showLinesFi> {}
}
